import { Link } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { ShieldCheck, Cookie, Bot, ArrowLeft } from "lucide-react";

const DPO_EMAIL = "compliance@assietica.it";

function LegalLayout({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <div className="min-h-screen bg-bone" data-testid={testid}>
      <header className="sticky top-0 z-30 border-b border-border/60 bg-bone/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 md:px-6">
          <Brand />
          <Link to="/login" className="flex items-center gap-1.5 text-sm text-forest hover:text-terracotta">
            <ArrowLeft className="h-4 w-4" /> Torna al portale
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-12 md:px-6">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage text-forest"><Icon className="h-6 w-6" /></span>
          <div>
            <h1 className="font-heading text-3xl font-semibold text-forest">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          Bozza informativa a scopo di conformità. Prima della pubblicazione definitiva i presenti testi devono essere
          validati dal responsabile compliance/DPO e dal legale dell'intermediario.
        </div>
        <div className="prose-legal mt-8 space-y-6 text-sm leading-relaxed text-charcoal-800">{children}</div>
        <p className="mt-12 border-t border-border/60 pt-6 text-xs text-muted-foreground">
          Ultimo aggiornamento: giugno 2026 · Titolare del trattamento: Assietica · Contatti privacy: {DPO_EMAIL}
        </p>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-lg font-semibold text-forest">{title}</h2>
      <div className="mt-2 space-y-2 text-muted-foreground">{children}</div>
    </section>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalLayout icon={ShieldCheck} title="Informativa Privacy" testid="page-privacy"
      subtitle="Trattamento dei dati personali ai sensi del Regolamento UE 2016/679 (GDPR)">
      <Section title="1. Titolare del trattamento">
        <p>Il Titolare del trattamento è Assietica (di seguito il "Titolare"). Per ogni richiesta relativa ai dati personali
          è possibile scrivere a <b>{DPO_EMAIL}</b>.</p>
      </Section>
      <Section title="2. Categorie di dati trattati">
        <p>Trattiamo i dati che ci fornisci tramite i moduli del sito (nome, ente/organizzazione, email, telefono, note) e,
          nell'ambito delle richieste di preventivo e adesione, i dati necessari alla valutazione del rischio e alla gestione
          della copertura (es. dati dell'attività, valori/somme assicurate, sinistrosità pregressa). Ove necessario alla
          valutazione della copertura possono essere trattati dati particolari, previa idonea base giuridica.</p>
      </Section>
      <Section title="3. Finalità e basi giuridiche">
        <ul className="list-disc space-y-1 pl-5">
          <li>Riscontro alle richieste informative e invio della documentazione (art. 6.1.b GDPR — misure precontrattuali).</li>
          <li>Gestione di preventivi, proposte e adesioni assicurative (art. 6.1.b GDPR).</li>
          <li>Adempimenti di legge, anche in materia assicurativa e IVASS (art. 6.1.c GDPR).</li>
          <li>Eventuali comunicazioni commerciali, solo previo tuo consenso facoltativo (art. 6.1.a GDPR).</li>
        </ul>
      </Section>
      <Section title="4. Uso di sistemi di Intelligenza Artificiale">
        <p>Il Portale utilizza sistemi di IA con finalità informative e di supporto (assistente sulle coperture, sintesi di
          prodotto e FAQ, bozze di relazione tecnica a supporto dell'intermediario, contenuti divulgativi audio a voce
          sintetica). Tali sistemi <b>non assumono decisioni automatizzate</b> produttive di effetti giuridici sull'interessato
          ai sensi dell'art. 22 GDPR: la valutazione e l'assunzione del rischio sono sempre effettuate da personale umano
          qualificato. Maggiori dettagli nella <a href="/ai" className="text-terracotta underline">pagina dedicata all'uso dell'IA</a>.</p>
      </Section>
      <Section title="5. Destinatari e responsabili del trattamento">
        <p>I dati possono essere comunicati alle imprese assicurative mandanti, ad intermediari iscritti al RUI e a fornitori
          tecnologici che operano quali responsabili/sub‑responsabili ex art. 28 GDPR, tra cui i fornitori dei servizi di IA
          (OpenAI, Anthropic) per la sola elaborazione dei contenuti. Ove il trattamento avvenga al di fuori dello Spazio
          Economico Europeo, è assistito da adeguate garanzie (Clausole Contrattuali Standard della Commissione UE).</p>
      </Section>
      <Section title="6. Conservazione">
        <p>I dati sono conservati per il tempo necessario alle finalità indicate e nei termini previsti dagli obblighi di legge
          in materia assicurativa e amministrativa.</p>
      </Section>
      <Section title="7. Diritti dell'interessato">
        <p>Puoi esercitare in ogni momento i diritti di accesso, rettifica, cancellazione, limitazione, portabilità e opposizione
          (artt. 15‑22 GDPR) scrivendo a <b>{DPO_EMAIL}</b>. Hai inoltre diritto di proporre reclamo al Garante per la protezione
          dei dati personali.</p>
      </Section>
    </LegalLayout>
  );
}

export function CookiePolicy() {
  return (
    <LegalLayout icon={Cookie} title="Cookie Policy" testid="page-cookie"
      subtitle="Informativa sull'uso dei cookie e delle tecnologie simili">
      <Section title="1. Cosa sono i cookie">
        <p>I cookie sono piccoli file di testo che i siti visitati inviano al dispositivo dell'utente, dove vengono memorizzati
          per essere ritrasmessi agli stessi siti alla visita successiva.</p>
      </Section>
      <Section title="2. Cookie tecnici (necessari)">
        <p>Il Portale utilizza esclusivamente cookie tecnici e di sessione necessari al funzionamento del servizio (ad es. il
          cookie di autenticazione per l'area riservata). Tali cookie non richiedono consenso ai sensi della normativa vigente
          e non vengono utilizzati per finalità di profilazione.</p>
      </Section>
      <Section title="3. Cookie di terze parti e profilazione">
        <p>Il Portale non installa cookie di profilazione di terze parti. Qualora in futuro venissero introdotti strumenti di
          analisi o marketing, saranno attivati solo previo consenso dell'utente tramite apposito banner.</p>
      </Section>
      <Section title="4. Gestione dei cookie">
        <p>È possibile gestire o disabilitare i cookie tramite le impostazioni del proprio browser. La disabilitazione dei
          cookie tecnici può compromettere il corretto funzionamento dell'area riservata.</p>
      </Section>
      <Section title="5. Contatti">
        <p>Per informazioni sull'uso dei cookie e dei dati personali: <b>{DPO_EMAIL}</b>.</p>
      </Section>
    </LegalLayout>
  );
}

export function AiPolicy() {
  return (
    <LegalLayout icon={Bot} title="Uso dell'Intelligenza Artificiale" testid="page-ai"
      subtitle="Informativa di trasparenza ai sensi dell'art. 50 del Regolamento UE 2024/1689 (AI Act)">
      <Section title="1. Principi">
        <p>Utilizziamo sistemi di Intelligenza Artificiale come <b>strumenti di supporto</b> informativo e documentale. La
          responsabilità dei contenuti verso il cliente resta in capo all'intermediario iscritto al RUI. Nessuna decisione
          relativa all'assunzione del rischio, alla determinazione del premio o all'esito di una richiesta è affidata in modo
          automatizzato all'IA: la tariffazione è determinata da regole predefinite e la valutazione è sempre umana.</p>
      </Section>
      <Section title="2. Dove usiamo l'IA">
        <ul className="list-disc space-y-1 pl-5">
          <li><b>Assistente Tecnico</b>: risponde a domande sulle coperture elaborando esclusivamente le Condizioni di polizza (uso interno ai partner).</li>
          <li><b>Schede prodotto intelligenti e Punti d'attenzione</b>: sintesi e FAQ generate dalle fonti ufficiali (uso interno).</li>
          <li><b>Relazione tecnica</b>: bozza di documento a supporto dell'intermediario, sempre verificata da un operatore umano.</li>
          <li><b>Podcast formativi</b>: contenuti divulgativi con testo e <b>voce sintetica</b> generati dalle fonti ufficiali; il podcast rivolto al cliente finale è chiaramente etichettato come contenuto generato con IA.</li>
        </ul>
      </Section>
      <Section title="3. Trasparenza (art. 50 AI Act)">
        <p>Ti informiamo chiaramente quando interagisci con un sistema di IA e quando un contenuto (testo o audio) è generato
          artificialmente. I contenuti generati hanno finalità informativa e non costituiscono offerta al pubblico né
          consulenza personalizzata.</p>
      </Section>
      <Section title="4. Limiti e verifica umana">
        <p>I contenuti generati dall'IA possono contenere imprecisioni. Prima di ogni uso commerciale vengono verificati
          rispetto alle Condizioni di Assicurazione ufficiali, che prevalgono in ogni caso. Per una valutazione adeguata è
          sempre necessario il contatto con un consulente qualificato.</p>
      </Section>
      <Section title="5. Trattamento dei dati">
        <p>L'elaborazione dei contenuti può comportare il trattamento di dati da parte di fornitori tecnologici quali
          responsabili del trattamento, come descritto nell'<a href="/privacy" className="text-terracotta underline">Informativa Privacy</a>.
          Per informazioni: <b>{DPO_EMAIL}</b>.</p>
      </Section>
    </LegalLayout>
  );
}
